import { useRef, useCallback, useEffect } from 'react';

/* v7.262: CSS 를 별도 .css 파일로 두지 않고 런타임에 주입한다.
     이유: 이 프로젝트에는 .css import 전례가 없어 빌드 설정에서 지원하는지 확인할 수 없었다.
           MobileScan 이 쓰는 document.createElement('style') 패턴을 그대로 따른다.
           → 빌드 설정에 의존하지 않고, 이 파일 하나만 지우면 흔적이 안 남는다. */
const BORDER_GLOW_CSS = `/* ============================================================
   BorderGlow.css — v7.262
   ⚠ 원본 CSS 를 못 받아서 BorderGlow.jsx 가 쓰는 변수 이름에서 역산해 새로 작성했다.
      원본과 픽셀 단위로 같지는 않다. 원본 CSS 를 구하면 이 파일을 갈아끼우면 된다.

   JSX 가 꽂아주는 변수 (건드리지 말 것):
     --cursor-angle     커서 방향 (0deg = 12시, 시계방향)
     --edge-proximity   0~100. 100 = '보이는 카드' 테두리 이상, 0 = 정중앙
                        (v7.315: 바깥 패딩 박스가 아니라 보이는 카드가 기준이다)
     --cursor-x --cursor-y  커서 좌표(px). 행 스포트라이트 중심 (v7.316)
     --row-hover        0/1. 행 위에 커서가 있는지
     --glow-size        행 스포트라이트 반경(px)
     --run-duration     .glow-run 이 한 바퀴 도는 시간 (v7.319)
     --run-band         .glow-run 띠의 길이, 행 폭 대비 % (v7.321)
     --card-bg --border-radius --glow-padding --cone-spread
     --edge-sensitivity --fill-opacity
     --glow-color, --glow-color-60/-50/-40/-30/-20/-10
     --glow-hot         핫스팟용. glow-color 보다 밝다 (v7.317)
     --palette-1/2/3    항상 깔리는 은은한 테두리 색 (colors prop, 저알파) (v7.317)
   ============================================================ */

.border-glow-card {
  --edge-proximity: 0;
  --cursor-angle: 0deg;

  /* 테두리가 켜지는 정도.
     edge-sensitivity=30 이면 proximity 70 부터 켜지기 시작해 100 에서 최대. */
  --glow-on: clamp(
    0,
    calc((var(--edge-proximity) - (100 - var(--edge-sensitivity))) / var(--edge-sensitivity)),
    1
  );

  position: relative;
  border-radius: var(--border-radius);
  padding: var(--glow-padding);
  isolation: isolate;

  /* v7.317: 링에 칠할 물감을 한 번만 정의해서 링(.edge-light)과 블룸(::before)이 같이 쓴다.
       두 겹이다.
         ① 커서 방향의 밝은 호 — 한가운데를 --glow-hot 으로 띄워 흰빛 코어를 만든다
         ② 항상 깔리는 팔레트 링 — colors prop 을 저알파로. 예시 이미지처럼
            빛이 닿지 않는 쪽에도 은은한 테두리가 남는다 */
  --ring-paint:
    conic-gradient(
      from var(--cursor-angle) at 50% 50%,
      var(--glow-hot)       0deg,
      var(--glow-color)     calc(var(--cone-spread) * 0.35deg),
      var(--glow-color-60)  calc(var(--cone-spread) * 1deg),
      var(--glow-color-30)  calc(var(--cone-spread) * 1.8deg),
      var(--glow-color-10)  calc(var(--cone-spread) * 2.8deg),
      transparent           calc(var(--cone-spread) * 3.8deg),
      transparent           calc(360deg - var(--cone-spread) * 3.8deg),
      var(--glow-color-10)  calc(360deg - var(--cone-spread) * 2.8deg),
      var(--glow-color-30)  calc(360deg - var(--cone-spread) * 1.8deg),
      var(--glow-color-60)  calc(360deg - var(--cone-spread) * 1deg),
      var(--glow-color)     calc(360deg - var(--cone-spread) * 0.35deg),
      var(--glow-hot)       360deg
    ),
    conic-gradient(
      from 210deg at 50% 50%,
      var(--palette-1), var(--palette-2), var(--palette-3), var(--palette-1)
    );
}

/* ── 블룸 — 테두리 바로 바깥으로만 번지는 빛 ──────────────────────
   v7.317: 예전에는 7겹 메시 그라디언트를 카드보다 크게 깔고 크게 흐려서
     카드 주변이 통째로 물든 큰 얼룩이 됐다 (배경까지 뿌옇게 밀려났다).
     원본 예시는 '테두리에 붙은 얇은 빛' 이다.
   → 링과 같은 물감을 링과 같은 위치(inset: glow-padding)에 깔고 살짝만 흐린다.
     안쪽은 .border-glow-inner(불투명, z-index 1)가 덮으므로 바깥 번짐만 보인다. */
.border-glow-card::before {
  content: '';
  position: absolute;
  inset: var(--glow-padding);
  border-radius: var(--border-radius);
  background: var(--ring-paint);
  filter: blur(calc(var(--glow-padding) * 0.42));
  opacity: calc(var(--glow-on) * var(--fill-opacity));
  transition: opacity 0.25s ease-out;
  pointer-events: none;
  z-index: 0;
}

/* ── 테두리 링 — 커서 방향이 가장 밝은 conic gradient ──────────────
   padding + mask-composite 로 '테두리만' 남긴다 (안쪽은 뚫린다). */
.border-glow-card .edge-light {
  position: absolute;
  inset: var(--glow-padding);
  border-radius: var(--border-radius);
  padding: var(--ring-width, 1.5px);
  pointer-events: none;
  z-index: 2;

  background: var(--ring-paint);

  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
          mask-composite: exclude;

  /* v7.317: 최소 밝기 0.14 — 커서가 멀어도 얇은 테두리는 남는다.
     원본 예시에서 빛이 닿지 않는 쪽에도 선이 보이는 것과 같다. */
  opacity: calc(0.14 + var(--glow-on) * 0.86);
  transition: opacity 0.2s ease-out;
}

/* 링에 바로 붙는 짧은 번짐 (블룸 ::before 보다 안쪽·선명) */
.border-glow-card .edge-light::after {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: inherit;
  background: inherit;
  filter: blur(5px);
  opacity: 0.7;
}

/* ── 실제 내용 — 카드 배경 위에 올린다 ────────────────────────── */
.border-glow-card .border-glow-inner {
  position: relative;
  z-index: 1;
  border-radius: var(--border-radius);
  background: var(--card-bg);
  height: 100%;
}

/* animated 인트로 스윕 중에는 transition 을 끈다 (JS 가 매 프레임 값을 준다) */
.border-glow-card.sweep-active::before,
.border-glow-card.sweep-active .edge-light {
  transition: none;
}

/* ============================================================
   .glow-row — 표 '행'용 경량 버전
   ⚠ 왜 따로 있나: BorderGlow 컴포넌트는 children 을 .border-glow-inner 로
     한 번 감싼다. 배송시트지 행은 display:grid 라서 감싸면 10칸이 한 칸으로
     뭉개진다. 그래서 행에는 DOM 을 안 늘리고 ::after 만 얹는다.
     (position:absolute 라 grid item 으로 잡히지 않는다 — 배치에 영향 없음)
   ============================================================ */
.glow-row {
  --edge-proximity: 0;
  --cursor-angle: 0deg;
  --glow-on: clamp(
    0,
    calc((var(--edge-proximity) - (100 - var(--edge-sensitivity, 30))) / var(--edge-sensitivity, 30)),
    1
  );
  position: relative;
}

.glow-row::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: var(--row-radius, 6px);
  padding: var(--ring-width, 1.5px);
  pointer-events: none;
  z-index: 3;

  /* v7.316: conic-gradient(각도 기준) → radial-gradient(커서 위치 기준).
       ⚠ 왜 바꿨나 — conic 은 '각도' 를 균등하게 나누지만 테두리의 '길이' 는 균등하지 않다.
         배송시트지 행처럼 납작한 박스(예: 1300×60)에서는 위쪽 테두리 하나가
         중심에서 ±87° 를 차지한다. cone-spread=25 로 계산하면:
             60% 밝기 θ=15° → 중앙에서 ±  8px  (테두리 폭의 1.2%)
             30% 밝기 θ=35° → 중앙에서 ± 21px  (3.2%)
             10% 밝기 θ=60° → 중앙에서 ± 52px  (8.0%)
         즉 1300px 테두리에서 실제로 밝은 구간이 16px 짜리 '점' 이었다.
         게다가 θ가 커질수록 tan 이 폭주해서, 커서를 좌우로 옮기면 빛이
         튀듯 날아가고 대부분 화면 밖으로 빠졌다. 그래서 '가운데에서만 약하게' 보였다.
       → 커서 좌표에 원을 놓으면 빛의 크기가 픽셀로 고정된다. 박스 비율과 무관하다.
         (--cursor-x/y 는 useRowGlow 가 px 로 꽂아준다) */
  background: radial-gradient(
    circle var(--glow-size, 200px) at var(--cursor-x, 50%) var(--cursor-y, 50%),
    var(--glow-color)     0%,
    var(--glow-color-60)  22%,
    var(--glow-color-30)  45%,
    var(--glow-color-10)  68%,
    transparent           100%
  );

  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
          mask-composite: exclude;

  /* 호버 중이면 항상 보이되, 가장자리에 가까울수록 더 밝게.
     예전엔 opacity 를 glow-on 하나로만 정해서, 납작한 행에서는
     세로 중앙 ±9px 를 벗어나야 겨우 켜졌다 (행 높이의 30%). */
  opacity: calc(var(--row-hover, 0) * (0.5 + 0.5 * var(--glow-on)));
  transition: opacity 0.2s ease-out;
}

/* ── v7.319: 테두리를 도는 빛 — 출고요청처럼 '아직 안 끝난' 행 표시용 ──────────
   호버와 무관하게 항상 돈다. .glow-row 의 ::after(호버 스포트라이트)와 겹쳐 쓴다.
   (::after 는 커서용, ::before 는 애니메이션용. 둘 다 absolute 라 grid 배치에 영향 없음)

   ⚠ conic 으로 '진짜 한 바퀴' 를 돌리지 않은 이유:
     행은 1240×60 처럼 극단적으로 납작하다. v7.316 에서 확인했듯 conic 은 각도를
     균등하게 나누는데 테두리 길이는 그렇지 않아, 위/아래 변에서만 빛이 뭉치고
     좌우 변은 순식간에 지나가 버린다. 가로로 쓸어주는 편이 실제로 '도는' 것처럼 보인다
     (둘레의 95%가 가로변이다).

   레이어 2장:
     ① 움직이는 밝은 띠 (background-size 220% + background-position 애니메이션)
     ② 항상 깔리는 옅은 테두리 — 띠가 지나가지 않는 동안에도 행이 표시되도록 */
/* v7.322: 두 겹으로 갈랐다.
     .glow-mark — 상태를 알리는 '상시 테두리'. 호버와 무관하게 늘 켜져 있다.
     .glow-run  — 여기에 도는 빛을 얹는다. 체크박스로 끌 수 있다.
   ⚠ 예전에는 둘이 한 덩어리라, 애니메이션을 끄면 출고요청 표시까지 통째로 사라졌다. */
.glow-row.glow-mark::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: var(--row-radius, 6px);
  padding: var(--ring-width, 1.5px);
  pointer-events: none;
  z-index: 3;

  background-image: linear-gradient(var(--glow-color-20) 0 100%);
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: 0 0;

  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
          mask-composite: exclude;
}

.glow-row.glow-mark.glow-run::before {
  /* v7.321: 시계방향 순회.
       레이어 3장이다.
         A 위쪽 절반을 왼→오 로 지나가는 띠   (background-size: 22% 50%, Y=0%)
         B 아래쪽 절반을 오→왼 로 지나가는 띠 (background-size: 22% 50%, Y=100%)
         C 항상 깔리는 옅은 테두리
       한 주기의 앞 절반은 A 만, 뒤 절반은 B 만 화면에 있다.
       쉬는 쪽은 pct 125%(오른쪽 밖)에 세워 둔다 → 위를 오른쪽으로 빠져나간 빛이
       곧바로 아래 오른쪽에서 다시 들어온다. 그래서 ↻ 로 읽힌다.

       ⚠ conic-gradient 로 '진짜 한 바퀴' 를 돌리지 않은 이유는 v7.316 과 같다.
         행은 1240×120 처럼 납작해서, 중심에서 보면 위/아래 변이 각각 174°를
         차지하고 좌우 변은 각각 5° 뿐이다. 각도를 균일하게 돌리면 빛이
         위아래 변 한가운데서 기어가다 모서리에서 폭주한다.
       ⚠ 좌우 변을 따로 이동시키지 않은 것도 의도다. 둘레 2720px 중 좌우 변은
         240px(8.8%)이고, 모서리 전환은 순간이라 눈에 띄지 않는다.
         (띠가 모서리에 걸칠 때 세로 테두리도 함께 밝아진다)

       좌표: background-size 가 22% 일 때 여백은 78%W 이므로
         띠 왼쪽 끝 x = 0.78W * pct.  pct -25% → 화면 왼쪽 밖, 125% → 오른쪽 밖. */
  background-image:
    linear-gradient(90deg,
      transparent 0%,
      var(--glow-color-10) 18%,
      var(--glow-color-30) 34%,
      var(--glow-color)    46%,
      var(--glow-hot)      50%,
      var(--glow-color)    54%,
      var(--glow-color-30) 66%,
      var(--glow-color-10) 82%,
      transparent 100%),
    linear-gradient(90deg,
      transparent 0%,
      var(--glow-color-10) 18%,
      var(--glow-color-30) 34%,
      var(--glow-color)    46%,
      var(--glow-hot)      50%,
      var(--glow-color)    54%,
      var(--glow-color-30) 66%,
      var(--glow-color-10) 82%,
      transparent 100%),
    linear-gradient(var(--glow-color-20) 0 100%);
  background-size: var(--run-band, 22%) 50%, var(--run-band, 22%) 50%, 100% 100%;
  background-repeat: no-repeat, no-repeat, no-repeat;
  background-position: -25% 0%, 125% 100%, 0 0;

  /* ⚠ 단축 속성(animation: … var(--x) …) 대신 낱개 속성을 쓴다.
       단축 안에 var() 가 들어가면 치환 결과가 조금이라도 어긋날 때
       선언 전체가 통째로 무효가 되고, 애니메이션이 '조용히' 안 걸린다. */
  animation-name: glowRun;
  animation-duration: var(--run-duration, 2.6s);
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  will-change: background-position;
}

/* ↻ 시계방향: 위쪽 왼→오 (앞 절반) → 아래쪽 오→왼 (뒤 절반) */
@keyframes glowRun {
  0%   { background-position: -25% 0%, 125% 100%, 0 0; }
  50%  { background-position: 125% 0%, 125% 100%, 0 0; }
  100% { background-position: 125% 0%,  -25% 100%, 0 0; }
}

/* 항상 켜두는 모드 (호버와 무관하게 은은히)
   — 커서가 없을 땐 스포트라이트가 행 중앙에 멈춰 있으므로, 균일한 밑바탕을 한 겹 깐다 */
.glow-row.glow-always::after {
  background:
    radial-gradient(
      circle var(--glow-size, 200px) at var(--cursor-x, 50%) var(--cursor-y, 50%),
      var(--glow-color)     0%,
      var(--glow-color-60)  22%,
      var(--glow-color-30)  45%,
      var(--glow-color-10)  68%,
      transparent           100%
    ),
    linear-gradient(var(--glow-color-20) 0 100%);
  opacity: calc(0.4 + var(--row-hover, 0) * 0.6);
}

/* v7.322: 라이트 모드 차단 제거.
     예전에는 '라이트에서는 배경에 묻힌다' 며 CSS 로 아예 껐는데,
     묻히는 원인은 모드가 아니라 '색' 이었다. 흰 배경에 밝기 70% 짜리 형광색을
     올리니 안 보였던 것이다.
     → App.js 가 테마별로 다른 HSL 팔레트를 꽂는다 (SHIP_GLOW_HUES_LIGHT/DARK).
       라이트는 밝기를 낮춰 진한 색으로, 다크는 지금처럼 밝은 색으로.
     끄고 싶으면 [행 글로우] 토글을 쓰면 된다 (이제 테마와 무관하게 보인다). */

@media (prefers-reduced-motion: reduce) {
  .border-glow-card::before,
  .border-glow-card .edge-light,
  .glow-row::after {
    transition: none;
  }
  /* v7.320: 도는 빛(.glow-run)은 여기서 빼낸다.
       v7.319 는 여기서 animation:none + 단색 배경으로 덮어써 버렸는데,
       OS 에서 '애니메이션 줄이기' 가 켜져 있으면 (윈도우 기본값인 PC 도 있다)
       이 규칙이 이겨서 '움직이지 않는 붉은 테두리' 만 남았다.
       이 효과는 '아직 안 끝난 출고' 를 알리는 기능성 표시라 끄면 의미가 없고,
       [행 글로우] 토글로 이미 사용자가 직접 끌 수 있다. */
}
`;

let __glowStyleInjected = false;
function injectGlowStyle() {
  if (__glowStyleInjected) return;
  if (typeof document === 'undefined') return;
  if (document.getElementById('border-glow-style')) { __glowStyleInjected = true; return; }
  const el = document.createElement('style');
  el.id = 'border-glow-style';
  el.textContent = BORDER_GLOW_CSS;
  document.head.appendChild(el);
  __glowStyleInjected = true;
}

function parseHSL(hslStr) {
  const match = hslStr.match(/([\d.]+)\s*([\d.]+)%?\s*([\d.]+)%?/);
  if (!match) return { h: 40, s: 80, l: 80 };
  return { h: parseFloat(match[1]), s: parseFloat(match[2]), l: parseFloat(match[3]) };
}

function buildGlowVars(glowColor, intensity) {
  const { h, s, l } = parseHSL(glowColor);
  const base = `${h}deg ${s}% ${l}%`;
  const opacities = [100, 60, 50, 40, 30, 20, 10];
  const keys = ['', '-60', '-50', '-40', '-30', '-20', '-10'];
  const vars = {};
  for (let i = 0; i < opacities.length; i++) {
    vars[`--glow-color${keys[i]}`] = `hsl(${base} / ${Math.min(opacities[i] * intensity, 100)}%)`;
  }
  /* v7.317: 핫스팟 — 커서가 가리키는 지점만 더 밝고 덜 진하게(흰빛에 가깝게).
     원본 예시의 '가장 밝은 곳이 흰색에 가까운' 느낌이 여기서 나온다. */
  vars['--glow-hot'] = `hsl(${h}deg ${Math.max(s - 22, 0)}% ${Math.min(l + 20, 98)}% / 100%)`;
  return vars;
}

/* v7.317: colors prop → 항상 깔리는 은은한 테두리 색.
     예전에는 이 색들을 7겹 메시 radial-gradient 로 써서 카드 뒤에 큰 얼룩을 만들었다.
     이제는 링을 도는 conic 의 밑바탕으로만 쓴다. 저알파라 튀지 않는다. */
function hexToRgb(hex) {
  let h = String(hex || '').trim().replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const n = parseInt(h, 16);
  if (!Number.isFinite(n) || h.length !== 6) return [192, 132, 252];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function buildPaletteVars(colors, intensity) {
  const list = (colors && colors.length ? colors : ['#c084fc', '#f472b6', '#38bdf8']);
  const alpha = Math.min(0.34 * (intensity || 1), 0.85);
  const vars = {};
  for (let i = 0; i < 3; i++) {
    const [r, g, b] = hexToRgb(list[Math.min(i, list.length - 1)]);
    vars[`--palette-${i + 1}`] = `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
  }
  return vars;
}

/* v7.317: --gradient-one … --gradient-seven / --gradient-base (7겹 메시) 제거.
     카드 주변을 통째로 물들이던 큰 얼룩의 원인이었다. colors 는 buildPaletteVars 로 간다. */

function easeOutCubic(x) { return 1 - Math.pow(1 - x, 3); }
function easeInCubic(x) { return x * x * x; }

function animateValue({ start = 0, end = 100, duration = 1000, delay = 0, ease = easeOutCubic, onUpdate, onEnd }) {
  const t0 = performance.now() + delay;
  function tick() {
    const elapsed = performance.now() - t0;
    const t = Math.min(elapsed / duration, 1);
    onUpdate(start + (end - start) * ease(t));
    if (t < 1) requestAnimationFrame(tick);
    else if (onEnd) onEnd();
  }
  setTimeout(() => requestAnimationFrame(tick), delay);
}

/* ────────────────────────────────────────────────────────────────
   v7.262: 행(row)용 글로우 훅
   BorderGlow 컴포넌트는 children 을 .border-glow-inner 로 감싼다.
   배송시트지 행은 display:grid 라서 감싸면 10칸이 한 칸으로 뭉개진다.
   → DOM 을 안 늘리고, 행 엘리먼트에 CSS 변수만 꽂는다. 렌더는 ::after 가 한다.

   쓰는 법:
     const glow = useRowGlow({ glowColor:'265 90 72', ... });
     <div className={'glow-row'} style={{...glow.style, ...기존스타일}}
          onPointerMove={glow.onPointerMove} onPointerLeave={glow.onPointerLeave}>

   ⚠ 성능: onPointerMove 는 커서 아래 행 하나에서만 돈다 (React 이벤트 위임).
      100행을 그려도 매 프레임 계산은 1행치다. setProperty 만 하고 리렌더는 없다.
   ──────────────────────────────────────────────────────────────── */
/* v7.318: CSS 변수 묶음만 만드는 순수 함수.
     배송시트지처럼 '행마다 색이 다른' 곳에서 색깔 수만큼 훅을 부르지 않으려고 뺐다.
     포인터 핸들러는 currentTarget 에만 쓰므로 색과 무관하다 → 훅은 한 번만 부르고
     style 은 이 함수로 상태별로 미리 만들어 두면 된다. */
export function rowGlowVars({
  glowColor = '265 90 72',
  glowIntensity = 1.0,
  coneSpread = 25,
  edgeSensitivity = 30,
  ringWidth = 1.5,
  rowRadius = 6,
  glowSize = 200,
  runDuration = 3,        // v7.319: 테두리를 도는 빛의 한 바퀴 시간(초). .glow-run 에서만 쓴다.
  runBand = 22,           // v7.321: 도는 띠의 길이 (행 폭 대비 %)
} = {}) {
  return {
    '--edge-sensitivity': edgeSensitivity,
    '--cone-spread': coneSpread,
    '--ring-width': `${ringWidth}px`,
    '--row-radius': `${rowRadius}px`,
    '--glow-size': `${glowSize}px`,
    '--run-duration': `${runDuration}s`,
    '--run-band': `${runBand}%`,
    ...buildGlowVars(glowColor, glowIntensity),
  };
}

export function useRowGlow(opts = {}) {
  useEffect(() => { injectGlowStyle(); }, []);

  const onPointerMove = useCallback((e) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const dx = (e.clientX - rect.left) - cx;
    const dy = (e.clientY - rect.top) - cy;

    let kx = Infinity, ky = Infinity;
    if (dx !== 0) kx = cx / Math.abs(dx);
    if (dy !== 0) ky = cy / Math.abs(dy);
    const edge = Math.min(Math.max(1 / Math.min(kx, ky), 0), 1);

    let deg = 0;
    if (dx !== 0 || dy !== 0) {
      deg = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
      if (deg < 0) deg += 360;
    }
    el.style.setProperty('--edge-proximity', (edge * 100).toFixed(2));
    el.style.setProperty('--cursor-angle', `${deg.toFixed(2)}deg`);
    // v7.316: 스포트라이트 중심 = 커서 좌표(px). 각도 대신 이 값이 빛 위치를 정한다.
    el.style.setProperty('--cursor-x', `${(e.clientX - rect.left).toFixed(1)}px`);
    el.style.setProperty('--cursor-y', `${(e.clientY - rect.top).toFixed(1)}px`);
    el.style.setProperty('--row-hover', '1');
  }, []);

  const onPointerLeave = useCallback((e) => {
    e.currentTarget.style.setProperty('--edge-proximity', '0');
    e.currentTarget.style.setProperty('--row-hover', '0');
  }, []);

  return {
    onPointerMove,
    onPointerLeave,
    style: rowGlowVars(opts),
  };
}

const BorderGlow = ({
  children,
  className = '',
  edgeSensitivity = 30,
  glowColor = '40 80 80',
  backgroundColor = '#120F17',
  borderRadius = 28,
  glowRadius = 40,
  glowIntensity = 1.0,
  coneSpread = 25,
  animated = false,
  colors = ['#c084fc', '#f472b6', '#38bdf8'],
  fillOpacity = 0.5,
}) => {
  const cardRef = useRef(null);
  useEffect(() => { injectGlowStyle(); }, []);

  const getCenterOfElement = useCallback((el) => {
    const { width, height } = el.getBoundingClientRect();
    return [width / 2, height / 2];
  }, []);

  /* v7.315: 근접도의 기준을 '보이는 카드' 로 바꾼다.
       .border-glow-card 는 padding: glowRadius(26px) 로 투명 여백을 두르고 있고,
       눈에 보이는 카드(.border-glow-inner)와 빛나는 링(.edge-light)은 그 안쪽에 있다.
       그런데 기존 계산은 여백까지 포함한 '바깥 박스' 를 기준으로 삼았다.
       → 보이는 테두리에 커서를 올려도 근접도가 0.77~0.88 밖에 안 나오고,
         최대값 1.0 은 아무것도 안 보이는 여백에서 나온다.
         edgeSensitivity=30 이면 위쪽 테두리에서 밝기가 0.24,
         감도를 15 로 낮추면 아예 0 이다. 그래서 테두리로 갈수록 빛이 꺼졌다.

       고친 뒤: 중앙 0 → 보이는 테두리 1.0 → 바깥 여백도 1.0 로 유지(클램프).
       각도 계산은 그대로 바깥 박스 중심 기준이다 (방향은 어차피 같다). */
  const getEdgeProximity = useCallback((el, x, y) => {
    const [cx, cy] = getCenterOfElement(el);
    // 보이는 카드의 반폭/반높이. glowRadius 가 카드보다 커지는 극단값에서도 0 나눗셈이 안 나게 최소 1.
    const hx = Math.max(cx - glowRadius, 1);
    const hy = Math.max(cy - glowRadius, 1);
    const nx = Math.abs(x - cx) / hx;
    const ny = Math.abs(y - cy) / hy;
    return Math.min(Math.max(Math.max(nx, ny), 0), 1);
  }, [getCenterOfElement, glowRadius]);

  const getCursorAngle = useCallback((el, x, y) => {
    const [cx, cy] = getCenterOfElement(el);
    const dx = x - cx;
    const dy = y - cy;
    if (dx === 0 && dy === 0) return 0;
    const radians = Math.atan2(dy, dx);
    let degrees = radians * (180 / Math.PI) + 90;
    if (degrees < 0) degrees += 360;
    return degrees;
  }, [getCenterOfElement]);

  const handlePointerMove = useCallback((e) => {
    const card = cardRef.current;
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const edge = getEdgeProximity(card, x, y);
    const angle = getCursorAngle(card, x, y);

    card.style.setProperty('--edge-proximity', `${(edge * 100).toFixed(3)}`);
    card.style.setProperty('--cursor-angle', `${angle.toFixed(3)}deg`);
  }, [getEdgeProximity, getCursorAngle]);

  /* v7.315: 커서가 카드를 벗어났을 때 끄기.
       기존엔 leave 핸들러가 없어 마지막 값이 그대로 얼어붙었다.
       인트로 스윕 직후 화면 어딘가에 빛이 남아 있다가, 커서를 올리는 순간
       중앙 쪽 낮은 값으로 뚝 떨어져 '가까이 가니 꺼진다' 로 보이기도 했다.
       (행용 useRowGlow 에는 원래 있던 처리다) */
  const handlePointerLeave = useCallback(() => {
    const card = cardRef.current;
    if (!card) return;
    card.style.setProperty('--edge-proximity', '0');
  }, []);

  useEffect(() => {
    if (!animated || !cardRef.current) return;
    const card = cardRef.current;
    const angleStart = 110;
    const angleEnd = 465;
    card.classList.add('sweep-active');
    card.style.setProperty('--cursor-angle', `${angleStart}deg`);

    animateValue({ duration: 500, onUpdate: v => card.style.setProperty('--edge-proximity', v) });
    animateValue({ ease: easeInCubic, duration: 1500, end: 50, onUpdate: v => {
      card.style.setProperty('--cursor-angle', `${(angleEnd - angleStart) * (v / 100) + angleStart}deg`);
    }});
    animateValue({ ease: easeOutCubic, delay: 1500, duration: 2250, start: 50, end: 100, onUpdate: v => {
      card.style.setProperty('--cursor-angle', `${(angleEnd - angleStart) * (v / 100) + angleStart}deg`);
    }});
    animateValue({ ease: easeInCubic, delay: 2500, duration: 1500, start: 100, end: 0,
      onUpdate: v => card.style.setProperty('--edge-proximity', v),
      onEnd: () => card.classList.remove('sweep-active'),
    });
  }, [animated]);

  const glowVars = buildGlowVars(glowColor, glowIntensity);

  return (
    <div
      ref={cardRef}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      className={`border-glow-card ${className}`}
      style={{
        '--card-bg': backgroundColor,
        '--edge-sensitivity': edgeSensitivity,
        '--border-radius': `${borderRadius}px`,
        '--glow-padding': `${glowRadius}px`,
        '--cone-spread': coneSpread,
        '--fill-opacity': fillOpacity,
        ...glowVars,
        ...buildPaletteVars(colors, glowIntensity),
      }}
    >
      <span className="edge-light" />
      <div className="border-glow-inner">
        {children}
      </div>
    </div>
  );
};

export default BorderGlow;
